import { ArrowRight, Clock, FileText, LogIn, LogOut, RefreshCw, Shuffle, FileSignature } from "lucide-react";
import { formatDate } from "@/lib/api";
import { TIPO_COM } from "@/components/contracts/ContractsTable";

const byStart = (a, b) => (a.data_inizio || a.created_at || "").localeCompare(b.data_inizio || b.created_at || "");

const EVENT = {
  assunzione: { label: "Assunzione", color: "bg-emerald-500", ring: "ring-emerald-200", icon: LogIn },
  proroga: { label: "Proroga", color: "bg-sky-500", ring: "ring-sky-200", icon: RefreshCw },
  trasformazione: { label: "Trasformazione", color: "bg-amber-500", ring: "ring-amber-200", icon: Shuffle },
  cessazione: { label: "Cessazione", color: "bg-rose-500", ring: "ring-rose-200", icon: LogOut },
  "": { label: "Contratto", color: "bg-slate-500", ring: "ring-slate-200", icon: FileSignature },
};

const Change = ({ label, from, to }) => (
  <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-900 ring-1 ring-inset ring-amber-200" data-testid={`timeline-change-${label.toLowerCase()}`}>
    {label}: <s className="opacity-60">{from ?? "—"}</s> <ArrowRight className="h-3 w-3" /> <b>{to ?? "—"}</b>
  </span>
);

const months = (a, b) => {
  if (!a || !b) return null;
  const d1 = new Date(a), d2 = new Date(b);
  const m = (d2.getFullYear() - d1.getFullYear()) * 12 + d2.getMonth() - d1.getMonth();
  return m >= 1 ? `${m} ${m === 1 ? "mese" : "mesi"}` : `${Math.max(1, Math.round((d2 - d1) / 86400000))} giorni`;
};

const changes = (prev, c) => {
  if (!prev) return [];
  const out = [];
  const cmp = (k, label, norm = (v) => v) => {
    const a = prev[k], b = c[k];
    if (a != null && b != null && a !== "" && b !== "" && norm(a) !== norm(b)) out.push({ label, from: a, to: b });
  };
  cmp("ore_settimanali", "Ore");
  cmp("mansione", "Mansione", (v) => String(v).toLowerCase());
  cmp("livello", "Livello", (v) => String(v).toLowerCase());
  cmp("tipologia_contratto", "Contratto", (v) => String(v).toLowerCase());
  cmp("tipo_orario", "Orario", (v) => String(v).toLowerCase());
  return out;
};

export const HoursTimeline = ({ contracts }) => {
  const items = [...contracts].filter((c) => ["contratto", "unilav"].includes(c.tipo_documento)).sort(byStart);
  if (items.length < 1) return null;
  const first = items[0], last = items[items.length - 1];
  const ended = last.tipo_comunicazione === "cessazione";
  const span = months(first.data_inizio, ended ? (last.data_fine || last.data_inizio) : new Date().toISOString().slice(0, 10));
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="hours-timeline">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Clock className="h-4 w-4" /> Storia contrattuale</h4>
        <p className="text-xs text-slate-500" data-testid="timeline-summary">{items.length} {items.length === 1 ? "evento" : "eventi"}{span ? ` · ${span} ${ended ? "di rapporto" : "in forza"}` : ""}{ended ? " · rapporto cessato" : ""}</p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
        {Object.entries(EVENT).filter(([k]) => k).map(([k, v]) => <span key={k} className="inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${v.color}`} /> {v.label}</span>)}
      </div>
      <ol className="relative mt-4 space-y-5 border-l-2 border-slate-200 pl-5">
        {items.map((c, i) => {
          const prev = items[i - 1];
          const ev = EVENT[c.tipo_comunicazione] || EVENT[""];
          const Icon = ev.icon;
          const diff = changes(prev, c);
          const gap = prev && c.data_inizio && prev.data_inizio ? months(prev.data_inizio, c.data_inizio) : null;
          return (
            <li key={c.id} className="relative" data-testid={`timeline-item-${c.id}`}>
              <span className={`absolute -left-[29px] top-0.5 grid h-5 w-5 place-items-center rounded-full ${ev.color} text-white ring-4 ${ev.ring}`}><Icon className="h-3 w-3" /></span>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                  {c.data_inizio ? formatDate(c.data_inizio, false) : "data ?"} · {TIPO_COM[c.tipo_comunicazione] || ev.label}
                  {gap ? <span className="normal-case tracking-normal text-slate-400"> · dopo {gap}</span> : null}
                </p>
                {c.has_file && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${c.id}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-medium text-[#3d6e49] hover:underline" data-testid={`timeline-file-${c.id}`}><FileText className="h-3 w-3" /> {c.tipo_documento === "unilav" ? "UNILAV" : "Contratto"}</a>}
              </div>
              <p className="mt-0.5 text-sm text-slate-900">
                <b>{c.ore_settimanali ?? "—"} h/sett.</b>{c.mansione ? ` · ${c.mansione}` : ""}{c.livello ? ` · liv. ${c.livello}` : ""}{c.tipologia_contratto ? ` · ${c.tipologia_contratto}` : ""}
              </p>
              {(c.data_fine || c.tipo_comunicazione === "cessazione") && (
                <p className="text-xs text-slate-500">
                  {c.tipo_comunicazione === "cessazione" ? `Rapporto concluso il ${formatDate(c.data_fine || c.data_inizio, false)}` : `Scadenza ${formatDate(c.data_fine, false)}${months(c.data_inizio, c.data_fine) ? ` · durata ${months(c.data_inizio, c.data_fine)}` : ""}`}
                </p>
              )}
              {diff.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1.5">{diff.map((d) => <Change key={d.label} {...d} />)}</div>}
            </li>
          );
        })}
      </ol>
    </section>
  );
};
